/**
 * The action vocabulary — every intent type the world understands.
 *
 * These live in the sim rather than in `@annwn/protocol` because this is where
 * they *originate*: an intent is a request to change the world, and the world
 * is what decides what a request may say. The protocol package re-exports
 * them, so client and server share one definition rather than two that drift
 * (M0-03's acceptance criterion).
 *
 * Each type ships with its parser here, next to the payload it parses. The
 * handler that acts on it lives with the rules it belongs to — movement in
 * `movement.ts` (M0-04), say and examine in M0-06 — because parsing is a
 * statement about the wire and applying is a statement about the game.
 */
import { isDirection } from './actor.js';
/**
 * Every intent type, as string literals. Combat (M2-01) and interaction
 * (M3-04) append here; nothing is ever renamed, because old clients and
 * archived event logs both spell them this way.
 */
export const INTENT_TYPES = [
    'join',
    'leave',
    'move',
    'say',
    'examine',
    'engage',
    'disengage',
    'attack',
    'guard',
    'sneak',
    // M2-07. What you may do to a body that cannot stop you: bind it, rob it,
    // finish it, move it. Four verbs rather than one `interact` with a verb field,
    // because each has a different payload and a different legality, and a single
    // handler that switched on a string would be four handlers wearing a coat.
    'staunch',
    'loot',
    'execute',
    'drag',
    // M3-03. The peaceful half of the loop. A verb of its own rather than a mode
    // of `interact`, for the reason the four above are four: it targets ground
    // rather than a body, and a handler that switched on that would be two
    // handlers wearing a coat.
    'forage',
    // M3-05. Strike a light. A verb of its own for the reason `forage` is one: it
    // targets what you are carrying rather than a body or a tile, and its
    // legality is a question about the weather that no other verb asks.
    'kindle',
    // M3-06. The camp and satchel: lay a fire, feed it, cook, butcher, eat,
    // equip or drop what is carried, and lie down. Separate verbs rather than one
    // `camp` with a mode field, for
    // the reason the four helpless-body verbs are four — each has a different
    // payload and a different legality, and one handler switching on a string
    // would be several handlers wearing a coat.
    'build',
    'feed',
    'cook',
    'butcher',
    'eat',
    'equip',
    'drop',
    'rest',
    // M4-01/02. The medicine ladder: press a bleed, bind it, wash it clean, sew
    // it, or burn it shut. Five verbs rather than one `treat` with a mode, for the
    // reason the helpless-body four are four — each asks a different thing of the
    // treater (a bandage, clean water, a needle, a hot iron) and refuses for a
    // different reason, and one handler on a string would be five in a coat.
    // Unlike the M2-07 four they may name the treater themselves as the target:
    // first aid is something you do to yourself as readily as to a fallen friend.
    'bandage',
    'clean',
    'suture',
    'cauterize',
    // M4-03. Make something at a fire out of what you gathered: a poultice, a
    // brew. A verb of its own rather than a mode of `cook`, because what it asks
    // of the world is different — cooking wants raw meat and this wants a herb and
    // a fire — and because the two would immediately have wanted different
    // channel times, different refusals and different prose.
    'prepare',
    // M4-05. The surgery, and the word that permits it. `extract` and `amputate`
    // are two verbs rather than one `operate` for the reason the ladder is five:
    // one wants a knife and a lodged fragment, the other an axe and a fire, and
    // they fail for different reasons in different sentences. `consent` is not a
    // verb of surgery at all — it is the patient's half of the conversation, and
    // the only intent in the game whose whole payload is an answer.
    'extract',
    'amputate',
    'consent',
    // M4-06. Getting the wounded home: take hold, lift onto a shoulder, or let
    // go. Three verbs rather than one `haul` with a mode, for the reason the
    // ladder is five — each asks a different thing of the body doing it, refuses
    // for a different reason, and reads as a different act to everyone watching.
    'carry',
    'release',
    // M4-07. What the mute have instead of words. A verb of its own rather than a
    // `say` with a flag, because it is not speech: it has no text, it cannot be
    // overheard through a wall, and everybody has it — the mark takes the words
    // away and grants nothing, which is the honest reading of the Second Branch.
    'gesture',
    // M4-07. Put somebody in the ground, or take them back out of it. Two verbs
    // rather than one `dig` with a mode, for the reason every pair here is two:
    // one is a service and the other is a crime, and they must refuse for
    // different reasons and read differently in the log.
    'bury',
    'dig',
    // M4-08. Ask somebody who lives here for something. One verb with a topic
    // rather than a verb per service, and this is the one place that is right:
    // the topics are *the same conversation*, they refuse for the same reason
    // (she is not there, she is asleep, she is dead), and the menu is drawn from
    // the same list the payload is validated against.
    'talk',
    // RC2-04. The reward ladder's two verbs, against the hazel deadfall: take the
    // axe cached in it, and chop the fallen wood down. Two verbs rather than one,
    // for the reason the camp four are four — `salvage` is instant and asks only
    // that a tool be there to take, `chop` is a job that asks for the axe already
    // in hand — and because the first is what *opens* woodcraft and the second is
    // what *builds* it.
    'salvage',
    'chop',
];
/** Long enough for a Welsh name, short enough not to break a log line. */
export const MAX_NAME_LENGTH = 24;
/** Long enough for any part id a body plan can hold; short enough to bound. */
export const MAX_PART_ID_LENGTH = 32;
export const MAX_SAY_LENGTH = 240;
/** Ids are content ids, not free text; bounded for the same reason parts are. */
export const MAX_ITEM_ID_LENGTH = 64;
/** Narrows an unknown value to a plain object, the shape every payload has. */
function asObject(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return null;
    return value;
}
/**
 * Control and formatting characters, matched by Unicode category rather than
 * by codepoint range. `\p{Cc}` covers the C0/C1 controls a naive
 * `[\x00-\x1f]` would; `\p{Cf}` additionally catches the invisible formatting
 * characters — zero-width joiners, bidi overrides — that a range misses and
 * that are the interesting half of a spoofed name.
 */
const INVISIBLE = /[\p{Cc}\p{Cf}]/gu;
/**
 * Collapses runs of whitespace and strips the invisibles, so a name or a chat
 * line cannot smuggle newlines into the log pane or spoof a UI element.
 */
export function sanitizeText(value, maxLength) {
    if (typeof value !== 'string')
        return null;
    const cleaned = value.replace(INVISIBLE, ' ').replace(/\s+/g, ' ').trim();
    if (cleaned.length === 0)
        return null;
    return cleaned.slice(0, maxLength);
}
export function parseJoin(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const name = sanitizeText(payload['name'], MAX_NAME_LENGTH);
    if (name === null)
        return null;
    const kind = payload['kind'];
    if (kind !== undefined && kind !== 'player' && kind !== 'creature')
        return null;
    // Not parsed field by field: a loadout never arrives off the wire (see
    // `JoinPayload`), and a validator here would be a validator implying it does.
    const loadout = payload['loadout'];
    let carried;
    if (payload['carried'] !== undefined) {
        if (!Array.isArray(payload['carried']))
            return null;
        const parsed = [];
        for (const value of payload['carried']) {
            const item = asObject(value);
            if (!item)
                return null;
            const itemId = item['itemId'];
            const count = item['count'];
            if (typeof itemId !== 'string' || itemId.length === 0)
                return null;
            if (!Number.isSafeInteger(count) || count <= 0)
                return null;
            parsed.push({ itemId, count: count });
        }
        carried = parsed;
    }
    // Not parsed either, and for the reason above squared: `vitals` is a body,
    // and `parseClientIntent` refuses `join` from clients outright.
    const vitals = payload['vitals'];
    const raw = payload['packId'];
    const packId = typeof raw === 'string' && raw.length > 0 ? raw : undefined;
    let at;
    if (payload['at'] !== undefined) {
        const position = asObject(payload['at']);
        if (!position)
            return null;
        const { x, y } = position;
        if (!Number.isInteger(x) || !Number.isInteger(y))
            return null;
        at = { x: x, y: y };
    }
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'join',
        payload: {
            name,
            ...(kind ? { kind: kind } : {}),
            ...(at ? { at } : {}),
            ...(loadout ? { loadout } : {}),
            ...(carried ? { carried } : {}),
            ...(vitals ? { vitals } : {}),
            ...(packId ? { packId } : {}),
        },
    };
}
export function parseLeave(intent) {
    return { actorId: intent.actorId, tick: intent.tick, type: 'leave', payload: {} };
}
export function parseMove(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const direction = payload['direction'];
    if (!isDirection(direction))
        return null;
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'move',
        payload: { direction },
    };
}
/**
 * The gestures (M4-07), and the whole vocabulary of them.
 *
 * Three, and deliberately three: enough to say *there*, *come*, and *danger*,
 * which is the set a silent person actually needs in a fight, and few enough
 * that the party invents the rest — which is the social mechanic the mute mark
 * exists to produce. Free-form emotes are not this and are not in the MVP.
 */
export const GESTURES = ['point', 'beckon', 'warn'];
export function isGesture(value) {
    return typeof value === 'string' && GESTURES.includes(value);
}
export function parseGesture(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const kind = payload['kind'];
    if (!isGesture(kind))
        return null;
    const direction = payload['direction'];
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'gesture',
        payload: { kind, ...(isDirection(direction) ? { direction } : {}) },
    };
}
export function parseSay(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const text = sanitizeText(payload['text'], MAX_SAY_LENGTH);
    if (text === null)
        return null;
    return { actorId: intent.actorId, tick: intent.tick, type: 'say', payload: { text } };
}
/**
 * An attack, as it arrives. Every field is optional on the wire and every one
 * of them is narrowed here, because this is the payload a hostile client has
 * the most to gain from bending: an unbounded `part` string reaches the wound
 * tables, and a `targetId` that is not a string reaches the actor table.
 */
export function parseAttack(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const rawTarget = payload['targetId'];
    const rawDirection = payload['direction'];
    const rawPart = payload['part'];
    let targetId = null;
    if (rawTarget !== undefined && rawTarget !== null) {
        if (typeof rawTarget !== 'string' || rawTarget.length === 0)
            return null;
        targetId = rawTarget;
    }
    let direction = null;
    if (rawDirection !== undefined && rawDirection !== null) {
        if (!isDirection(rawDirection))
            return null;
        direction = rawDirection;
    }
    // One or the other. Neither names a target, and both is a client that has
    // not decided what it is asking for.
    if ((targetId === null) === (direction === null))
        return null;
    let part = null;
    if (rawPart !== undefined && rawPart !== null) {
        if (typeof rawPart !== 'string')
            return null;
        if (rawPart.length === 0 || rawPart.length > MAX_PART_ID_LENGTH)
            return null;
        part = rawPart;
    }
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'attack',
        payload: { targetId, direction, part },
    };
}
export function parseEngage(intent) {
    const attack = parseAttack({ ...intent, type: 'attack' });
    if (!attack || attack.payload.part !== null)
        return null;
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'engage',
        payload: { targetId: attack.payload.targetId, direction: attack.payload.direction },
    };
}
export function parseDisengage(intent) {
    const payload = asObject(intent.payload);
    if (!payload || Object.keys(payload).length !== 0)
        return null;
    return { actorId: intent.actorId, tick: intent.tick, type: 'disengage', payload: {} };
}
export function parseSneak(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const on = payload['on'];
    if (typeof on !== 'boolean')
        return null;
    return { actorId: intent.actorId, tick: intent.tick, type: 'sneak', payload: { on } };
}
export function parseGuard(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const on = payload['on'];
    if (typeof on !== 'boolean')
        return null;
    return { actorId: intent.actorId, tick: intent.tick, type: 'guard', payload: { on } };
}
/* ------------------------------------------------- the helpless (M2-07) -- */
/** The half every verb against a body shares: a target id that is really one. */
function parseTargetId(payload) {
    const raw = payload['targetId'];
    if (typeof raw !== 'string' || raw.length === 0)
        return null;
    return raw;
}
/**
 * The five medicine verbs, which share a payload and differ only in what they
 * are called (M4). Every field of `MedicalPayload` is optional — see its
 * comment — so this narrows what is present and defaults the rest at resolution.
 *
 * `targetId` absent is *legal*, and means the treater themselves: the one place
 * the helpless-body parser would refuse an omission, this one welcomes it.
 */
function parseMedical(type) {
    return (intent) => {
        const payload = asObject(intent.payload);
        if (!payload)
            return null;
        let targetId;
        const rawTarget = payload['targetId'];
        if (rawTarget !== undefined) {
            if (typeof rawTarget !== 'string' || rawTarget.length === 0)
                return null;
            targetId = rawTarget;
        }
        let part;
        const rawPart = payload['part'];
        if (rawPart !== undefined) {
            if (typeof rawPart !== 'string' ||
                rawPart.length === 0 ||
                rawPart.length > MAX_PART_ID_LENGTH) {
                return null;
            }
            part = rawPart;
        }
        return {
            actorId: intent.actorId,
            tick: intent.tick,
            type,
            payload: {
                ...(targetId === undefined ? {} : { targetId }),
                ...(part === undefined ? {} : { part }),
            },
        };
    };
}
export const parseStaunch = parseMedical('staunch');
export const parseBandage = parseMedical('bandage');
export const parseClean = parseMedical('clean');
export const parseSuture = parseMedical('suture');
export const parseCauterize = parseMedical('cauterize');
export const parseExtract = parseMedical('extract');
export const parseAmputate = parseMedical('amputate');
/** The patient's yes or no (M4-05). Both fields required; neither is defaulted. */
export function parseConsent(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const surgeonId = parseTargetId({ targetId: payload['surgeonId'] });
    if (surgeonId === null)
        return null;
    const allow = payload['allow'];
    if (typeof allow !== 'boolean')
        return null;
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'consent',
        payload: { surgeonId, allow },
    };
}
export function parseLoot(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const targetId = parseTargetId(payload);
    if (targetId === null)
        return null;
    const itemId = payload['itemId'];
    if (typeof itemId !== 'string')
        return null;
    if (itemId.length === 0 || itemId.length > MAX_ITEM_ID_LENGTH)
        return null;
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'loot',
        payload: { targetId, itemId },
    };
}
/**
 * Forage takes no payload at all — you gather what is under your feet.
 *
 * Considered and rejected: a coordinate, so a player could forage the tile
 * beside them. Reaching for a plant you are not standing on is a small
 * convenience that costs the one thing the verb is *for* — being committed,
 * in the open, somewhere specific, for several seconds. The channel already
 * cancels if you move; letting you pick from a tile away would make "move" and
 * "forage" overlap in a way the cancellation rule cannot express.
 */
export function parseForage(intent) {
    return { actorId: intent.actorId, tick: intent.tick, type: 'forage', payload: {} };
}
/** No payload either — see `KindleIntent`. */
export function parseKindle(intent) {
    return { actorId: intent.actorId, tick: intent.tick, type: 'kindle', payload: {} };
}
/* ------------------------------------------------------- the camp (M3-06) -- */
/** The payload-free camp verbs, which differ only in what they are called. */
function parseBare(type) {
    return (intent) => ({ actorId: intent.actorId, tick: intent.tick, type, payload: {} });
}
export function parseBury(intent) {
    return { actorId: intent.actorId, tick: intent.tick, type: 'bury', payload: {} };
}
export function parseDig(intent) {
    return { actorId: intent.actorId, tick: intent.tick, type: 'dig', payload: {} };
}
export function parseTalk(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const targetId = parseTargetId(payload);
    if (targetId === null)
        return null;
    const topic = payload['topic'];
    if (topic !== undefined && (typeof topic !== 'string' || topic.length > MAX_ITEM_ID_LENGTH)) {
        return null;
    }
    const subject = payload['subject'];
    if (subject !== undefined &&
        (typeof subject !== 'string' || subject.length > MAX_ITEM_ID_LENGTH)) {
        return null;
    }
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'talk',
        payload: {
            targetId,
            ...(typeof topic === 'string' ? { topic } : {}),
            ...(typeof subject === 'string' ? { subject } : {}),
        },
    };
}
export const parseBuild = parseBare('build');
export const parseFeed = parseBare('feed');
export const parseCook = parseBare('cook');
export const parseButcher = parseBare('butcher');
export const parsePrepare = parseBare('prepare');
export const parseSalvage = parseBare('salvage');
export const parseChop = parseBare('chop');
export function parseEat(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const itemId = payload['itemId'];
    if (typeof itemId !== 'string')
        return null;
    if (itemId.length === 0 || itemId.length > MAX_ITEM_ID_LENGTH)
        return null;
    return { actorId: intent.actorId, tick: intent.tick, type: 'eat', payload: { itemId } };
}
export function parseDrop(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const stackKey = payload['stackKey'];
    if (typeof stackKey !== 'string' || stackKey.length === 0 || stackKey.length > 200)
        return null;
    return { actorId: intent.actorId, tick: intent.tick, type: 'drop', payload: { stackKey } };
}
export function parseEquip(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const stackKey = payload['stackKey'];
    const stow = payload['stow'];
    if (stow === true && stackKey === undefined) {
        return { actorId: intent.actorId, tick: intent.tick, type: 'equip', payload: { stow: true } };
    }
    if (stow !== undefined)
        return null;
    if (typeof stackKey !== 'string' || stackKey.length === 0 || stackKey.length > 200)
        return null;
    return { actorId: intent.actorId, tick: intent.tick, type: 'equip', payload: { stackKey } };
}
export function parseRest(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const on = payload['on'];
    if (typeof on !== 'boolean')
        return null;
    return { actorId: intent.actorId, tick: intent.tick, type: 'rest', payload: { on } };
}
export function parseExecute(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const targetId = parseTargetId(payload);
    if (targetId === null)
        return null;
    // Exactly `true`, not merely truthy. The point of a confirmation is that it
    // cannot be arrived at by accident, and `confirm: 1` is an accident.
    if (payload['confirm'] !== true)
        return null;
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'execute',
        payload: { targetId, confirm: true },
    };
}
export function parseDrag(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const targetId = parseTargetId(payload);
    if (targetId === null)
        return null;
    // Accepted and ignored (M4-06). A client that still names a direction is not
    // wrong, it is out of date, and refusing it would break it for nothing.
    const direction = payload['direction'];
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'drag',
        payload: { targetId, ...(isDirection(direction) ? { direction } : {}) },
    };
}
/** Lift a body (M4-06). One field, and it names the body. */
export function parseCarry(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const targetId = parseTargetId(payload);
    if (targetId === null)
        return null;
    return { actorId: intent.actorId, tick: intent.tick, type: 'carry', payload: { targetId } };
}
/** Let go (M4-06). No payload: you are holding one thing or none. */
export function parseRelease(intent) {
    return { actorId: intent.actorId, tick: intent.tick, type: 'release', payload: {} };
}
export function parseExamine(intent) {
    const payload = asObject(intent.payload);
    if (!payload)
        return null;
    const { x, y } = payload;
    if (!Number.isInteger(x) || !Number.isInteger(y))
        return null;
    return {
        actorId: intent.actorId,
        tick: intent.tick,
        type: 'examine',
        payload: { x: x, y: y },
    };
}
//# sourceMappingURL=actions.js.map